// Shared, dependency-free file-upload validation. Lives here (not in
// lib/api.ts, which imports authFetch from lib/auth.ts) so both api.ts and
// auth.ts's uploadFileWithProgress can validate a file identically without
// creating an api.ts <-> auth.ts circular import.

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "image/jpeg",
  "image/png",
  "image/webp",
];

const ALLOWED_EXTENSIONS = [".pdf", ".txt", ".text", ".docx", ".doc", ".jpg", ".jpeg", ".png", ".webp"];

export function validateFileType(file: File): { isValid: boolean; error?: string } {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  // Check MIME type
  if (!ALLOWED_MIME_TYPES.includes(fileType)) {
    // Check file extension as fallback
    const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));
    if (!hasValidExtension) {
      return {
        isValid: false,
        error: `Unsupported file type. Please upload a PDF, Word doc, text file, or image (JPG/PNG/WEBP). Received: ${fileType || "unknown type"}`,
      };
    }
  }

  // Additional check for HTML files disguised as other types
  if (fileType === "text/html" || fileName.includes(".html") || fileName.includes(".htm")) {
    return {
      isValid: false,
      error: "HTML files are not supported. Please upload a PDF, Word doc, text file, or image.",
    };
  }

  // Check file size (launch-safe 50MB cap)
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      isValid: false,
      error: `File too large. Maximum size is 50MB; this file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`,
    };
  }

  return { isValid: true };
}

/**
 * Maps an upload error's message text to a short, specific headline. Upload
 * failure UIs across the app (Snap & Study, the main Library uploader) used
 * to show a static "Something went wrong" title no matter what actually
 * happened, with the real reason buried as small subtext underneath —
 * scanning the screen, a network drop and a 50MB file looked identical.
 * The underlying error strings themselves already got specific in
 * uploadDocument()/uploadFileWithProgress() (lib/api.ts, lib/auth.ts); this
 * turns that specificity into a headline instead of a generic banner.
 */
export function classifyUploadError(message: string): { headline: string; detail: string } {
  const m = message.toLowerCase();
  if (m.includes("timed out")) {
    return { headline: "Upload timed out", detail: "The connection was too slow to finish. Try again on a stronger connection, or with a smaller file." };
  }
  if (m.includes("network connection lost") || m.includes("failed to fetch") || m.includes("network error")) {
    return { headline: "Connection lost", detail: "We lost the connection mid-upload. Nothing was saved, so you can simply try again." };
  }
  if (m.includes("trial limit") || m.includes("upgrade to plus")) {
    return { headline: "Free document limit reached", detail: "The free plan keeps up to 5 documents. Delete one you no longer need, or upgrade to Plus for unlimited uploads." };
  }
  if (m.includes("too large")) return { headline: "File too large", detail: "Files can be up to 50 MB. Try splitting the document or compressing the PDF." };
  if (m.includes("storage_limit") || (m.includes("storage") && m.includes("limit"))) {
    return { headline: "Your library is full", detail: "Delete a few documents you no longer need, or upgrade for more space." };
  }
  if (m.includes("unsupported file") || m.includes("not supported") || m.includes("invalid file type")) {
    return { headline: "Unsupported file", detail: "Novari can read PDF, Word, plain text, JPG, PNG and WEBP files." };
  }
  if (m.includes("storage") && (m.includes("unavailable") || m.includes("failed to upload file to storage"))) {
    return { headline: "Storage unavailable", detail: "Our file storage is having a moment. Your file wasn't saved. Try again shortly." };
  }
  if (m.includes("server is having trouble") || m.includes("server error") || m.includes("try again in a moment")) {
    return { headline: "Server busy", detail: "Novari is busy right now. Try again in a minute." };
  }
  return { headline: "Upload didn't finish", detail: "Something went wrong on our side. Your other documents are safe. Try again." };
}
